import { BASE_URL } from "../../config";

/**
 * Parses article catalogue response from the new API format
 * @param {Object} catalogueResponse - Raw response from the API
 * @returns {Object} Parsed catalogue with articles and metadata
 */
export const parseArticleCatalogue = (catalogueResponse) => {
  if (!catalogueResponse || !catalogueResponse.payload) {
    return {
      data: [],
      meta: { count: 0 },
    };
  }

  const { articles = [], pagination = {} } = catalogueResponse.payload;

  const parsedArticles = articles.map((article) => ({
    id: article.fileName || article.url, // Use fileName or url as ID since id field is missing
    title: article.title,
    created: article.created ? new Date(article.created) : new Date(),
    description: article.summary || article.excerpt || null,
    body: article.body,
    url: article.url || `/blog/${article.url}`,
    fileName: article.fileName,
    image: parseArticleImage(article.image || article.thumbnail), // Handle both image and thumbnail fields
    categories: parseArticleCategories(article.categories),
    category: article.categories?.map((cat) => cat.name).join(", "),
    author: parseArticleAuthor(article.author),
    hideScrollSpy: article.hideScrollSpy || false,
    metadata: article.metadata,
  }));

  return {
    data: parsedArticles,
    meta: {
      count: pagination.totalArticles || parsedArticles.length, // Changed from totalItems to totalArticles
      totalPages: pagination.totalPages || 1,
      currentPage: pagination.currentPage || 0,
      itemsPerPage: pagination.itemsPerPage || parsedArticles.length,
    },
  };
};

/**
 * Parses individual article details from the new API format
 * @param {Object} articleResponse - Raw response from the API
 * @returns {Object} Parsed article details
 */
export const parseArticleDetails = (articleResponse) => {
  if (!articleResponse) {
    return null;
  }

  // Handle multiple response formats:
  // 1. { payload: { article: {...} } } - wrapped format
  // 2. { article: {...} } - unwrapped format from BlogService
  // 3. {...} - direct article object
  const article =
    articleResponse.payload?.article || // Format 1
    articleResponse.article || // Format 2
    articleResponse; // Format 3

  return {
    id: article.fileName || article.url, // Use fileName or url as ID
    title: article.title,
    alias: article.url,
    created: article.created ? new Date(article.created) : new Date(),
    body: article.body,
    html: article.body, // For backward compatibility
    summary: article.summary,
    description: article.summary || article.excerpt,
    url: article.url || `/blog/${article.url}`,
    fileName: article.fileName,
    image: parseArticleImage(article.image || article.thumbnail), // Handle both image and thumbnail fields
    categories: parseArticleCategories(article.categories),
    category: article.categories?.map((cat) => cat.name).join(", "),
    author: parseArticleAuthor(article.author),
    hideScrollSpy: article.hideScrollSpy || false,
    fieldScrollspyVisible: !article.hideScrollSpy, // Inverted for compatibility
    metadata: article.metadata,
    pdf: article.pdf,
    googleDriveId: article.googleDriveId,
    driveId: article.googleDriveId, // For backward compatibility
    inlineImages: article.inlineImages || {},
  };
};

/**
 * Parses article image data
 * @param {Object|string} image - Raw image data (can be base64 string or object)
 * @returns {Object} Parsed image
 */
const parseArticleImage = (image) => {
  if (!image) {
    return {
      fid: null,
      uuid: null,
      url: null,
      imageFile: null,
      alt: null,
    };
  }

  // Handle base64 image data (thumbnail from API)
  if (typeof image === "string" && image.startsWith("data:image")) {
    return {
      fid: null,
      uuid: null,
      url: image, // Use the base64 string directly as URL
      imageFile: image,
      alt: "",
    };
  }

  // Handle object-based image data
  if (typeof image === "object") {
    // Handle new format with imageName and data fields
    if (image.data && image.imageName) {
      return {
        fid: null,
        uuid: null,
        url: image.data, // Use the base64 data as URL
        imageFile: image.data,
        alt: image.imageName || "",
      };
    }

    // Handle old format
    return {
      fid: image.id || image.fid,
      uuid: image.id || image.uuid,
      url:
        image.url || (image.imageFile ? `${BASE_URL}${image.imageFile}` : null),
      imageFile: image.imageFile || image.url,
      alt: image.altText || image.alt || "",
    };
  }

  // Fallback for unexpected format
  return {
    fid: null,
    uuid: null,
    url: null,
    imageFile: null,
    alt: null,
  };
};

/**
 * Parses article categories
 * @param {Array} categories - Raw categories array
 * @returns {Array} Parsed categories
 */
const parseArticleCategories = (categories) => {
  if (!categories || !Array.isArray(categories)) {
    return [];
  }

  return categories.map((cat) => ({
    id: cat.id,
    uuid: cat.id, // For backward compatibility
    name: cat.name,
    description: cat.description,
  }));
};

/**
 * Parses article author data
 * @param {Object} author - Raw author data
 * @returns {Object} Parsed author
 */
const parseArticleAuthor = (author) => {
  if (!author) {
    return {
      uuid: null,
      uid: null,
      name: "Unknown",
      created: null,
      changed: null,
      image: null,
    };
  }

  return {
    uuid: author.id || author.uuid,
    uid: author.id || author.uid,
    name: author.name || author.displayName || "Unknown",
    created: author.createdDate ? new Date(author.createdDate) : null,
    changed: author.modifiedDate ? new Date(author.modifiedDate) : null,
    image: author.image
      ? parseArticleImage(author.image)
      : {
          fid: null,
          uuid: author.profileImage,
          url: author.profileImage ? `${BASE_URL}${author.profileImage}` : null,
          alt: author.name || "Author",
        },
  };
};

/**
 * Maps search results to the expected format
 * @param {Object} searchResults - Search results from APIArticleService
 * @returns {Object} Mapped search results
 */
export const mapArticleSearchResults = (searchResults) => {
  if (!searchResults) {
    return {
      data: [],
      meta: { count: 0 },
    };
  }

  const articles = searchResults.articles || [];
  const metadata = searchResults.metadata || {};

  const mappedArticles = articles.map((article) => ({
    id: article.fileName || article.url, // Use fileName or url as ID
    title: article.title,
    created: article.created ? new Date(article.created) : new Date(),
    description: article.summary || article.excerpt || null,
    url: article.url || `/blog/${article.url}`,
    fileName: article.fileName, // Include fileName for fetching
    image: parseArticleImage(article.image || article.thumbnail), // Handle both image and thumbnail fields
    category: article.categories?.map((cat) => cat.name).join(", "),
    scrollspyVisible: !article.hideScrollSpy,
    author: parseArticleAuthor(article.author),
  }));

  return {
    data: mappedArticles,
    meta: {
      count: metadata.total || mappedArticles.length,
      totalPages: metadata.totalPages,
      currentPage: metadata.page,
      itemsPerPage: metadata.itemsPerPage,
    },
  };
};
